/**
 * Checks if an agreement is pending cancellation due to overdue installments
 * An agreement is pending cancellation if:
 * - It has an 'Active' status
 * - It has at least one unpaid installment
 * - That installment's due date is more than 3 days in the past
 *
 * @param {Object} agreement - The agreement object to check
 * @returns {boolean} - True if the agreement is pending cancellation, false otherwise
 */
export const isPendingCancellation = (agreement) => {
  if (!agreement || agreement.status !== 'Active') return false;

  if (!agreement.installments || agreement.installments.length === 0) return false;

  const now = new Date();

  return agreement.installments.some((installment) => {
    if (installment.isPaid) return false;

    const dueDate = new Date(installment.dueDate);
    const daysSinceDue = (now - dueDate) / (1000 * 60 * 60 * 24);

    return daysSinceDue > 3;
  });
};

/**
 * Checks if an agreement was recently approved (within 48 hours)
 *
 * @param {Object} agreement - The agreement object to check
 * @returns {boolean} - True if recently approved, false otherwise
 */
export const isRecentlyApproved = (agreement) => {
  if (agreement.status !== 'Active') return false;
  if (!agreement.histories || agreement.histories.length === 0) return false;

  const sortedHistories = [...agreement.histories].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const pendingIndex = sortedHistories.findIndex((history) => history.agreementStatus === 'PendingApprovalInternal');

  if (pendingIndex === -1) return false;

  const firstActiveAfterPending = sortedHistories
    .slice(pendingIndex + 1)
    .find((history) => history.agreementStatus === 'Active');

  if (!firstActiveAfterPending) return false;

  const approvalDate = new Date(firstActiveAfterPending.timestamp);
  const now = new Date();
  const hoursSinceApproval = (now - approvalDate) / (1000 * 60 * 60);

  return hoursSinceApproval <= 48;
};

/**
 * Checks if an agreement was recently rejected by internal (within 24 hours)
 *
 * @param {Object} agreement - The agreement object to check
 * @returns {boolean} - True if recently rejected, false otherwise
 */
export const isRecentlyRejectedByInternal = (agreement) => {
  if (!agreement.histories || agreement.histories.length === 0) return false;

  const rejectionHistory = agreement.histories.find((history) => history.closingReason === 'RejectedByInternal');

  if (!rejectionHistory) return false;

  const rejectionDate = new Date(rejectionHistory.timestamp);
  const now = new Date();
  const hoursSinceRejection = (now - rejectionDate) / (1000 * 60 * 60);

  return hoursSinceRejection <= 24;
};

/**
 * Checks if an agreement has a recent counter offer (within 48 hours)
 *
 * @param {Object} agreement - The agreement object to check
 * @returns {boolean} - True if has recent counter offer, false otherwise
 */
export const hasRecentCounterOffer = (agreement) => {
  if (agreement.status !== 'PendingApprovalPayer') return false;
  if (!agreement.histories || agreement.histories.length === 0) return false;

  const sortedHistories = [...agreement.histories].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const lastHistory = sortedHistories[sortedHistories.length - 1];

  if (lastHistory.agreementStatus !== 'PendingApprovalPayer') return false;

  const counterOfferDate = new Date(lastHistory.timestamp);
  const now = new Date();
  const hoursSinceCounterOffer = (now - counterOfferDate) / (1000 * 60 * 60);

  return hoursSinceCounterOffer <= 48;
};

/**
 * Checks if an agreement has an expired counter offer (48+ hours after closure)
 *
 * @param {Object} agreement - The agreement object to check
 * @returns {boolean} - True if has expired counter offer, false otherwise
 */
export const hasExpiredCounterOffer = (agreement) => {
  if (!agreement.histories || agreement.histories.length === 0) return false;

  const sortedHistories = [...agreement.histories].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  for (let i = 0; i < sortedHistories.length - 1; i++) {
    const currentHistory = sortedHistories[i];
    const nextHistory = sortedHistories[i + 1];

    if (
      currentHistory.agreementStatus === 'PendingApprovalPayer' &&
      nextHistory.agreementStatus === 'Closed' &&
      nextHistory.closingReason === 'PayerApprovalExpired'
    ) {
      const closedDate = new Date(nextHistory.timestamp);
      const now = new Date();
      const hoursSinceClosed = (now - closedDate) / (1000 * 60 * 60);

      return hoursSinceClosed >= 24;
    }
  }

  return false;
};

/**
 * Filters an array of agreements by a specific check function
 *
 * @param {Array} agreements - Array of agreement objects to filter
 * @param {Function} checkFunction - The function to use for filtering
 * @returns {Array} - Filtered array of agreements
 */
const filterAgreementsByCheck = (agreements, checkFunction) => {
  if (!agreements || agreements.length === 0) return [];
  return agreements.filter(checkFunction);
};

/**
 * Filters agreements to only those pending cancellation
 *
 * @param {Array} agreements - Array of agreement objects to filter
 * @returns {Array} - Filtered array of agreements that are pending cancellation
 */
export const getPendingCancellationAgreements = (agreements) => {
  return filterAgreementsByCheck(agreements, isPendingCancellation);
};

/**
 * Filters agreements to only those recently approved
 *
 * @param {Array} agreements - Array of agreement objects to filter
 * @returns {Array} - Filtered array of recently approved agreements
 */
export const getRecentlyApprovedAgreements = (agreements) => {
  return filterAgreementsByCheck(agreements, isRecentlyApproved);
};

/**
 * Filters agreements to only those recently rejected by internal
 *
 * @param {Array} agreements - Array of agreement objects to filter
 * @returns {Array} - Filtered array of recently rejected agreements
 */
export const getRecentlyRejectedAgreements = (agreements) => {
  return filterAgreementsByCheck(agreements, isRecentlyRejectedByInternal);
};

/**
 * Filters agreements to only those with recent counter offers
 *
 * @param {Array} agreements - Array of agreement objects to filter
 * @returns {Array} - Filtered array of agreements with recent counter offers
 */
export const getRecentCounterOfferAgreements = (agreements) => {
  return filterAgreementsByCheck(agreements, hasRecentCounterOffer);
};

/**
 * Main function to check all agreement banner conditions at once
 * Returns an object with boolean flags for each banner type
 *
 * @param {Array} agreements - Array of agreement objects to check
 * @returns {Object} - Object with boolean flags for each banner condition
 */
export const checkAgreementBanners = (agreements) => {
  return {
    shouldShowAgreementSuccessBanner: getRecentlyApprovedAgreements(agreements).length > 0,
    shouldShowClosingReasonBanner: getRecentlyRejectedAgreements(agreements).length > 0,
    shouldShowCounterOfferBanner: getRecentCounterOfferAgreements(agreements).length > 0,
    shouldShowOverdueInstallmentBanner: getPendingCancellationAgreements(agreements).length > 0,
  };
};

/**
 * Constants for the pending cancellation feature
 */
export const PENDING_CANCELLATION = {
  DAYS_THRESHOLD: 3,
  BANNER_TEXT: {
    TITLE: 'Fyrirhuguð gjaldfelling',
    MESSAGE:
      'Greiðslusamkomulag þitt er komið í vanskil og verður gjaldfellt ef ekki er brugðist við. Vinsamlega greiddu skuldina til að komast hjá gjaldfellingu samkomulagsins, en þá fara kröfur aftur í innheimtu með tilheyrandi kostnaði og verða skráðar á vanskilaskrá Creditinfo.',
  },
  TAG_LABEL: 'Fyrirhuguð gjaldfelling',
  TAG_COLOR: '#BA1A1A',
};
